import { CHART_TYPES } from '../Charts/chartConfig'
import { RESULT_TYPES } from '../questions.constants'
import { chartColors } from './chartThemes'
import ensureLabelLength from './ensureLabelLength'
import domtoimage from 'dom-to-image-more'
import { getFirstItem } from '@/utils/array'

export function exportChart(Pptxgen, results) {
  const presentation = new Pptxgen()
  results.forEach(result => {
    result.questions.forEach(question => {
      const isAValidType = [
        RESULT_TYPES.LINEAR,
        RESULT_TYPES.MATRIX,
        RESULT_TYPES.WORD_CLOUD,
      ].includes(question.resultType)

      if (!isAValidType) {
        return
      }

      const isMatrix = question.resultType === RESULT_TYPES.MATRIX

      const isWordCloud = question.resultType === RESULT_TYPES.WORD_CLOUD

      const isPercent = question?.chartSettings?.isPercent || false

      const typeChart = question?.chartSettings?.type || CHART_TYPES.BAR

      const isStackedBar = typeChart === CHART_TYPES.STACKED_BAR

      const theme = question?.chartSettings?.theme

      const chartSetting = chartSettings(isPercent)[getCharsComponent(typeChart)]

      const params = {
        question,
        presentation,
        isPercent,
        theme,
        chartSetting,
        isMatrix,
      }

      const exportChart = isMatrix ? matrixChart : linearChart

      const commonChart = isStackedBar ? getStackedBarExportChart : exportChart

      isWordCloud
        ? exportWordCloud({ ...params, groupCode: result.code })
        : commonChart(params)
    })
  })

  const HALF_SECOND = 500

  // setTimeout is being used to give time to add word cloud export. Without timeout word cloud not added to pptx file.
  setTimeout(() => {
    presentation.writeFile({ fileName: 'chart' })
  }, HALF_SECOND)
}

function linearChart({ question, presentation, isPercent, theme, chartSetting }) {
  const slide = presentation.addSlide()

  const labels = question.results.map(result => ensureLabelLength(result.label))

  const data = question.results.map(result =>
    isPercent ? Number(result.percentage.replace('%', '')) : result.count,
  )

  slide.addText(question.title, {
    x: 0.2,
    y: 0.5,
    color: '696969',
    fontSize: 16,
  })

  const options = {
    x: 0.8,
    y: 1.4,
    w: 6.0,
    h: 3.0,
    chartColors: chartColors(theme, question).map(color => color.replace('#', '')),
    ...chartSetting.optionsSettings,
  }

  if (isPercent) {
    options.valAxisMinVal = 0
    options.valAxisMaxVal = 100
  }

  slide.addChart(
    presentation.charts[chartSetting.chartType],
    [
      {
        name: '',
        labels: labels,
        values: data,
      },
    ],
    options,
  )
}

function matrixChart({ question, presentation, isPercent, theme, chartSetting }) {
  question.results.forEach(result => {
    const slideMatrix = presentation.addSlide()

    const labels = result.subAnswers.map(subAnswer => ensureLabelLength(subAnswer.label))

    const data = result.subAnswers.map(subAnswer =>
      isPercent ? Number(subAnswer.percentage.replace('%', '')) : subAnswer.count,
    )

    slideMatrix.addText(question.title, {
      x: 0.2,
      y: 0.5,
      color: '696969',
      fontSize: 16,
    })

    const label = result.label

    slideMatrix.addText(label, {
      x: 0.5,
      y: 1,
      color: '6b6b6b',
      fontSize: 14,
    })

    const options = {
      x: 0.8,
      y: 1.4,
      w: 6.0,
      h: 3.0,
      chartColors: chartColors(theme, question).map(color => color.replace('#', '')),
      ...chartSetting.optionsSettings,
    }

    if (isPercent) {
      options.valAxisMinVal = 0
      options.valAxisMaxVal = 100
    }

    slideMatrix.addChart(
      presentation.charts[chartSetting.chartType],
      [
        {
          name: '',
          labels: labels,
          values: data,
        },
      ],

      options,
    )
  })
}

function getStackedBarExportChart({
  question,
  presentation,
  isPercent,
  theme,
  isMatrix,
}) {
  const questionResults = isMatrix
    ? getFirstItem(question.results).subAnswers
    : question.results

  const hasOneColor = theme?.hasOneColor

  const labels = questionResults.map(result => ensureLabelLength(result.label))

  const labelDatasets = isMatrix
    ? question.results.map(result => ensureLabelLength(result.label))
    : ['']

  const slide = presentation.addSlide()

  slide.addText(question.title, {
    x: 0.2,
    y: 0.5,
    color: '696969',
    fontSize: 16,
  })

  const options = {
    x: 0.8,
    y: 1.4,
    w: 6.0,
    h: 3.0,
    barDir: 'bar',
    barGrouping: 'stacked',
    chartColors: theme?.colors,
    showLegend: true,
    valAxisMinVal: 0,
    valAxisMaxVal: isPercent ? 100 : question.results.count,
  }

  const datasets = questionResults.map((_, index) => ({
    name: labels[index],
    labels: labelDatasets,
    values: isMatrix
      ? question.results.map(result => {
          return isPercent
            ? Number(result.subAnswers[index].percentage.replace('%', ''))
            : result.subAnswers[index].count
        })
      : [
          isPercent
            ? Number(question.results[index].percentage.replace('%', ''))
            : question.results[index].count,
        ],

    backgroundColor: hasOneColor ? theme?.colors[0] : theme?.colors[index],
  }))

  slide.addChart(presentation.charts.BAR, datasets, options)
}

function chartSettings(isPercent) {
  const chartSettings = {
    bar: {
      chartType: 'BAR',

      optionsSettings: {
        barDir: 'col',
        showLegend: false,
      },
    },

    horizontalBar: {
      chartType: 'BAR',

      optionsSettings: {
        barDir: 'bar',
        showLegend: false,
      },
    },

    line: {
      chartType: 'LINE',

      optionsSettings: {
        showLegend: false,
        lineDataSymbolSize: 10,
        shadow: { type: 'none' },
      },
    },

    pie: {
      chartType: 'PIE',

      optionsSettings: {
        showLegend: true,
        showPercent: isPercent,
        showValue: !isPercent,
      },
    },
  }

  return chartSettings
}

async function exportWordCloud({ question, presentation, groupCode }) {
  const slide = presentation.addSlide()

  slide.addText(question.title, {
    x: 0.2,
    y: 0.2,
    color: '696969',
    fontSize: 10,
  })

  const image = await getWordCloudImage(groupCode, question.code)

  slide.addImage({
    data: image,
    x: 1,
    y: 0.8,
    w: '80%',
    h: '80%',
  })
}

function getWordCloudImage(groupCode, questionCode) {
  const node = document.querySelector(`#word-cloud-image-${groupCode}-${questionCode}`)

  return domtoimage.toPng(node)
}

function getCharsComponent(typeChart) {
  switch (typeChart) {
    case CHART_TYPES.BAR:
      return 'bar'
    case CHART_TYPES.BAR_HORIZONTAL:
      return 'horizontalBar'
    case CHART_TYPES.PIE:
      return 'pie'
    case CHART_TYPES.LINE:
      return 'line'
    case CHART_TYPES.STACKED_BAR:
      return 'stacked'
    default:
      throw new Error(`${typeChart} not a valid chart type`)
  }
}
